import { Injectable } from '@angular/core';
import { openDB, IDBPDatabase, deleteDB } from 'idb';
import { CACHE_IDB_CONFIG, CACHE_IDB_STORE_NAME, CacheDB } from './cache-Idb.constants';
// Optional Improvement: Implement string compression

@Injectable({
  providedIn: 'root',
})
export class CacheIdbService {
  private dbPromise: Promise<IDBPDatabase<CacheDB>>;
  private currentKey: string | null = null;

  constructor() {
    this.dbPromise = openDB<CacheDB>(CACHE_IDB_CONFIG.name, CACHE_IDB_CONFIG.version, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(CACHE_IDB_STORE_NAME)) {
          db.createObjectStore(CACHE_IDB_STORE_NAME);
        }
      },
    });
    
    // Initialize by checking if there's any existing key
    this.initializeCurrentKey();
  }

  private async initializeCurrentKey(): Promise<void> {
    const db = await this.dbPromise;
    const keys = await db.getAllKeys(CACHE_IDB_STORE_NAME);
    if (keys.length > 0) {
      this.currentKey = keys[0] as string;
      
      // If there are multiple keys (from previous usage), clean up extras
      if (keys.length > 1) {
        for (let i = 1; i < keys.length; i++) {
          await db.delete(CACHE_IDB_STORE_NAME, keys[i]);
        }
      }
    }
  }

  /** Store data - removes any existing data first */
  async setData(key: string, data: any): Promise<void> {
    const db = await this.dbPromise;
    
    // If there's already a key and it's different from the new one, delete it
    if (this.currentKey && this.currentKey !== key) {
      await db.delete(CACHE_IDB_STORE_NAME, this.currentKey);
    }
    
    // Store the new data
    await db.put(CACHE_IDB_STORE_NAME, data, key);
    this.currentKey = key;
  }

  /** Retrieve data */
  async getData<T>(key: string): Promise<T | null> {
    const db = await this.dbPromise;
    const data = await db.get(CACHE_IDB_STORE_NAME, key);
    return data ? data as T : null;
  }

  /** Delete a specific key */
  async removeData(key: string): Promise<void> {
    const db = await this.dbPromise;
    await db.delete(CACHE_IDB_STORE_NAME, key);
    if (this.currentKey === key) {
      this.currentKey = null;
    }
  }

  /** Clear all cached data */
  async clearCache(): Promise<void> {
    const db = await this.dbPromise;
    await db.close();
    await deleteDB(CACHE_IDB_CONFIG.name);
    this.currentKey = null;
  }
  
  /** Get the current key being stored */
  async getCurrentKey(): Promise<string | null> {
    return this.currentKey;
  }
}
